import { CONFIG } from "../config";
import { useApp } from "../state/AppState";
import { useScrollToSection } from "./Nav";

/**
 * Pied de page.
 *
 * **Réduit pendant le diagnostic.** Mesuré à 1 028 px sur téléphone, il pesait
 * 47 % de la hauteur défilable de l'écran des tâches et 54 % de celle du volet
 * 02 : au milieu d'un parcours, plus de la moitié de ce qu'on pouvait faire
 * défiler était un pied institutionnel. Ses colonnes de liens invitaient en
 * outre à quitter le diagnostic en cours, ce qu'aucun écran de parcours ne
 * devrait proposer.
 *
 * Ce qui reste partout : la marque, et l'attribution de l'adaptation de l'OIT.
 * Celle-là est une obligation de licence, pas une décoration — elle ne se
 * raccourcit pas pour gagner des pixels.
 */
export function Footer() {
  const { contactCourriel, dirigeantActif } = useApp();
  const { goTo, view } = useApp();
  const scrollTo = useScrollToSection();
  const parcours = view !== "home";

  return (
    <footer className={`site${parcours ? " reduit" : ""}`}>
      <div className="shell">
        <div className="cols">
          <div>
            <div className="brand">STRATERA</div>
            {/*
              La signature — 24/08/2026.

              Elle ne dit pas ce que le produit fait, elle dit comment il le
              fait : on ne juge pas un métier, on mesure des tâches. C'est la
              méthode entière en trois mots, et c'est déjà la phrase qui porte
              le titre de l'accueil — « elle prendra vos tâches, une par une ».
              Une marque s'installe en répétant, pas en variant.
            */}
            {/*
              Sans point final — corrigé le 24/08 sur capture.

              Aucun intitulé mono du site n'en porte, et l'interlettrage de
              .16em détache le point du dernier E : il flotte seul, à un espace
              de la lettre qu'il devrait suivre. La couverture des rapports et
              la carte de partage écrivaient déjà la signature sans point ; le
              pied de page était le seul à diverger.

              Le point reste dans le slogan écrit en prose — « Tâche par
              tâche. » — où il ferme une phrase. En capitales espacées, il ne
              ferme rien : il traîne.
            */}
            <div className="signature">Tâche par tâche</div>
            <p
              style={{
                fontSize: "14.5px",
                maxWidth: "36ch",
                color: "var(--text-inv-soft)",
              }}
            >
              {/*
                Ni quatre capitales, ni une ville — 24/08/2026.

                La version d'origine annonçait quatre capitales pour un marché
                ouvert dans une seule : une promesse d'implantation que rien ne
                tenait, règle n° 6. La correction a réduit à « Abidjan », et
                elle est allée trop loin dans l'autre sens.

                **Le produit n'est pas limité à Abidjan.** C'est une page web
                sans barrière géographique ; le référentiel couvre des métiers
                de tout le pays ; la boutique a déjà vendu au Burkina. Écrire
                « Abidjan » ne décrit donc rien de vrai — cela **exclut** un
                lecteur de Bouaké, de Korhogo ou de Ouagadougou, qui en conclut
                que ce n'est pas pour lui. C'est exactement la faute que le
                commentaire de `content/copy.ts` reproche à l'ancienne liste de
                métiers : parler « à un cadre d'Abidjan et à personne d'autre ».

                **Troisième version, et la deuxième justification était fausse
                elle aussi.** « Diagnostic calibré pour la Côte d'Ivoire »
                s'appuyait sur trois arguments : franc CFA, plan comptable
                SYSCOHADA, référentiel métier. Vérification faite, deux sur
                trois disent l'inverse de ce qu'on leur faisait dire :

                - le **franc CFA** est partagé par quatorze pays — huit de
                  l'UEMOA, six de la CEMAC ;
                - le droit comptable **OHADA** en couvre dix-sept.

                Ce ne sont pas des marqueurs ivoiriens, ce sont des marqueurs
                d'Afrique francophone : **ce qui rend le produit portable, pas
                ce qui le limite.** Seul le référentiel métier est réellement
                ivoirien — et c'est une précision supérieure sur les métiers
                courants, pas une barrière, puisque le champ est libre et que
                le modèle rédige pour n'importe quel métier.

                Reste à ne pas retomber dans la faute d'origine. Elle n'est pas
                d'annoncer large : elle était d'annoncer **quatre capitales**,
                c'est-à-dire quatre implantations dont trois n'existaient pas.
                « Pour les actifs d'Afrique francophone » énonce une audience,
                pas un bureau — et c'est vrai.

                Ce qui suit nomme donc ce qui est à la fois vrai et partagé par
                toute la zone, et dit le seul écart qui compte face aux produits
                européens : celui-ci n'a pas été traduit.

                Abidjan reste où elle doit être : au siège social, dans les
                mentions légales, où elle désigne une entité juridique réelle.
              */}
              Des outils numériques pour les actifs d'Afrique francophone.
              Franc CFA, droit OHADA, mobile money — pensés d'ici, pas traduits.
            </p>
          </div>
          {!parcours && (
          <div>
            <h4>Le diagnostic</h4>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                goTo("s-t1");
              }}
            >
              Votre exposition
            </a>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                goTo("s-t2");
              }}
            >
              Le coût de votre temps
            </a>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                goTo("s-t3");
              }}
            >
              Votre niveau de maîtrise
            </a>
            {/* L'axe dirigeant, enfin relié : une vraie adresse. */}
            {dirigeantActif && <a href="/dirigeant">Pour votre entreprise</a>}
          </div>
          )}
          {!parcours && (
          <div>
            <h4>En savoir plus</h4>
            {[
              ["comparatif", "Pourquoi nous"],
              ["methode", "Méthode et limites"],
              ["tarifs", "Tarifs"],
              ["faq", "Questions fréquentes"],
              ["legal", "Mentions légales et confidentialité"],
            ].map(([id, libelle]) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo(id);
                }}
              >
                {libelle}
              </a>
            ))}
            {/*
              Une vraie adresse, pas une ancre : c'est celle qu'on met en note de
              bas de page, et elle doit rester valable une fois citée.
            */}
            <a href="/barometre">Baromètre — les chiffres publiés</a>
            {/*
              Trois vraies adresses de plus, pas des ancres. La vérification
              d'attestation existait sans qu'aucun lien n'y mène — l'argument de
              crédibilité pour un tiers était introuvable ; les cinquante pages
              métier n'étaient reliées que par le plan de site.
            */}
            <a href="/metiers">Les métiers face à l'IA</a>
            <a href="/attestation">Vérifier une attestation</a>
            <a href="/a-propos">Qui édite STRATERA</a>
          </div>
          )}
        </div>
        {/*
          Le contact, atteignable sans déplier une section — 24/08/2026.

          L'adresse n'existait que dans les mentions légales, derrière un
          `<details>` replié, trois écrans plus bas. Un site qui encaisse doit
          pouvoir être joint depuis n'importe où, et c'est le pied de page qu'on
          cherche pour cela.
        */}
        {/*
          L'adresse nue devient une porte — 26/08/2026.

          Elle était atteignable, ce qui était déjà mieux qu'enfouie sous un
          `<details>`. Mais une adresse seule laisse tout le travail à celui qui
          a le problème : quoi écrire, à qui, et pour combien de temps. Le motif
          le plus fréquent — un rapport payé qui n'arrive pas — se règle sans
          nous écrire, et personne ne pouvait le savoir.
        */}
        <p className="footer-contact">
          Un rapport qui n'arrive pas, un remboursement, vos données :{" "}
          <a href="/aide">un problème, une demande</a> — ou directement{" "}
          <a href={`mailto:${contactCourriel}`}>{contactCourriel}</a>
        </p>
        <p className="legal">
          {/*
            L'aveu de simulation suit désormais l'état réel du paiement.

            Cette phrase était affichée sans condition. Le jour où l'encaissement
            réel s'ouvre, elle continue d'annoncer à chaque visiteur que le
            paiement est simulé — sur la page qui encaisse. Faux dans un sens
            comme dans l'autre : taire la simulation quand elle est active
            tromperait, l'annoncer quand elle ne l'est plus tuerait la vente.
          */}
          {CONFIG.paiementSimule
            ? "Moteur d'analyse réel, paiement simulé tant qu'aucun opérateur n'est branché. "
            : ""}
          Les valeurs
          de référence appliquées à
          chaque groupe professionnel sont dérivées de : Gmyrek, P. et al., 2025,{" "}
          <i>Generative AI and jobs: a refined global index of occupational exposure</i>, ILO
          Working Paper 140, Organisation internationale du Travail, licence CC BY 4.0. Il s'agit
          d'une adaptation : les professions ont été regroupées et les scores ramenés sur une autre
          échelle. Les opinions et points de vue exprimés ici relèvent de la seule responsabilité de
          RISEAFRICA et ne sont pas approuvés par l'OIT. Un indice élevé ne constitue pas une
          prévision de perte d'emploi.
        </p>
      </div>
    </footer>
  );
}
